/**
 * Player Module for TitCock.tv
 *
 * Handles:
 * - Video card creation
 * - Video playback controls
 * - Gesture handling (swipe up/down)
 * - Card positioning and animations
 * - Rate limiting for video loading
 */

import {
  createElement,
  clamp,
  getViewportHeight,
  translateY,
  sleep,
} from "./utils.js";

const getTranslateY = (el) => {
  if (!el) return 0;
  const style = window.getComputedStyle(el);
  const transform = style.transform;
  if (transform === "none" || !transform) return 0;

  try {
    const matrix = new DOMMatrix(transform);
    if (!isNaN(matrix.m42)) return matrix.m42;
  } catch (e) { }

  const match = transform.match(/matrix.*\((.+)\)/);
  if (match) {
    const values = match[1].split(/,\s*/);
    if (values.length === 6) return parseFloat(values[5]);
    if (values.length === 16) return parseFloat(values[13]);
  }
  return 0;
};
import {
  CONFIG,
  elements,
  playerState,
  savedState,
  getCards,
  getVideo,
  getNeighborCards,
  isGateVisible,
} from "./state.js";
import { buildVideoUrl } from "./feed.js";
import { isCardLiked } from "./storage.js";
import { updateHeader, updateMuteIcon } from "./ui.js";
import {
  failVastPlayback,
  isVastAdVideo,
  loadVastAd,
  trackVastClick,
  trackVastComplete,
  trackVastLoaded,
  trackVastPlaying,
  trackVastProgress,
} from "./vast.js";

const getCardHeight = () => elements.scroller?.clientHeight || getViewportHeight();

const hasUserActivation = () =>
  navigator.userActivation?.hasBeenActive ||
  navigator.userActivation?.isActive ||
  false;

const videoHasAudio = (vid) => {
  if (!vid) return false;
  if (vid.mozHasAudio === true) return true;
  if (vid.audioTracks && vid.audioTracks.length > 0) return true;
  return Number(vid.webkitAudioDecodedByteCount || 0) > 0;
};

const videoHasNoAudio = (vid) => {
  if (!vid || isVastAdVideo(vid)) return false;
  if (vid.mozHasAudio === false) return true;
  if (vid.audioTracks && vid.readyState >= 1) return vid.audioTracks.length === 0;
  if (vid.dataset.audioChecked === "1") return !videoHasAudio(vid);
  return false;
};

const getActiveVideo = () => {
  const cards = savedState.isViewerActive ? savedState.cards : getCards();
  const activeIdx = savedState.isViewerActive
    ? savedState.activeIndex
    : playerState.activeIndex;
  return getVideo(cards[activeIdx]);
};

export const activeVideoHasNoAudio = () => videoHasNoAudio(getActiveVideo());

const updateMuteAvailability = () => {
  const hasNoAudio = activeVideoHasNoAudio();
  elements.muteBtn?.classList.toggle("disabled", hasNoAudio);
  if (hasNoAudio && elements.muteIcon) {
    elements.muteIcon.src = "src/svg/volume-off.svg";
  }
};

export const markVideoAudioChecked = (vid, force = false) => {
  if (!vid) return;
  if (
    !force &&
    vid.mozHasAudio == null &&
    !vid.audioTracks
  ) {
    return;
  }
  vid.dataset.audioChecked = "1";
  updateMuteAvailability();
};

// ==========================================================================
// Rate Limiter
// ==========================================================================

const rateLimiter = {
  tokens: CONFIG.RATE_LIMIT,
  queue: [],
};

// Replenish tokens periodically
setInterval(() => {
  rateLimiter.tokens = Math.min(
    CONFIG.RATE_LIMIT,
    rateLimiter.tokens + CONFIG.RATE_LIMIT,
  );
  while (rateLimiter.tokens > 0 && rateLimiter.queue.length > 0) {
    rateLimiter.tokens--;
    const fn = rateLimiter.queue.shift();
    fn();
  }
}, 1000);

/**
 * Execute a function with rate limiting
 * @param {Function} fn - Function to execute
 */
const rateLimited = (fn) => {
  if (rateLimiter.tokens > 0) {
    rateLimiter.tokens--;
    fn();
  } else {
    rateLimiter.queue.push(fn);
  }
};

// ==========================================================================
// Video Cache Management
// ==========================================================================

/**
 * Add video to cache and manage cache size
 * @param {HTMLVideoElement} vid
 */
const addToCache = (vid) => {
  const idx = playerState.videoCache.indexOf(vid);
  if (idx !== -1) {
    playerState.videoCache.splice(idx, 1);
  }
  playerState.videoCache.push(vid);
  pruneCache();
};

/**
 * Remove old videos from cache to free memory
 */
const pruneCache = () => {
  const activeIndex = savedState.isViewerActive
    ? savedState.activeIndex
    : playerState.activeIndex;

  const neighbors = getNeighborCards(activeIndex);
  const keepVideos = new Set(neighbors.map(getVideo).filter(Boolean));

  while (playerState.videoCache.length > CONFIG.MAX_CACHED_VIDEOS) {
    const vid = playerState.videoCache.shift();
    if (keepVideos.has(vid)) {
      playerState.videoCache.push(vid);
      continue;
    }
    try {
      vid.pause();
    } catch { }
    if (vid.src) {
      vid.removeAttribute("src");
      vid.load();
    }
  }
};

// ==========================================================================
// Video Loading
// ==========================================================================

/**
 * Ensure a video is visible (set opacity and handle layout)
 * @param {HTMLVideoElement} vid
 */
export const ensureVideoVisible = (vid) => {
  if (!vid) return;
  vid.style.opacity = "1";
};

/**
 * Load video source if not already loaded
 * @param {HTMLVideoElement} vid
 * @param {boolean} [immediate=false] - Skip rate limiting
 */
export const loadVideo = (vid, immediate = false) => {
  if (!vid) return;

  // Only skip if src is already set to a valid URL AND we're not asking for immediate load
  if (vid.src === vid.dataset.src || (!immediate && vid.src && vid.src.startsWith("http"))) {
    ensureVideoVisible(vid);
    if (immediate && isActiveVideo(vid)) playVideo(vid);
    return;
  }

  const doLoad = () => {
    const rawSrc = vid.dataset.src;
    if (!rawSrc && isVastAdVideo(vid)) {
      loadVastAd(vid, { onFailure: removeFailedAdCard })
        .then(() => {
          if (vid.isConnected) loadVideo(vid, true);
        })
        .catch(() => {
          // The VAST module removes failed/no-fill items through onFailure.
        });
      return;
    }
    console.log(`[loadVideo] doLoad called, rawSrc:`, rawSrc);
    if (!rawSrc) {
      console.log(`[loadVideo] No rawSrc, returning from doLoad`);
      return;
    }

    console.log(`[loadVideo] Setting vid.src to:`, rawSrc);
    vid.src = rawSrc;
    vid.load();
    addToCache(vid);

    // Safety: if it's already got enough data, show it immediately
    if (vid.readyState >= 2) ensureVideoVisible(vid);

    console.log(`[loadVideo] Video load triggered, src:`, vid.src);
  };

  if (immediate) {
    doLoad();
  } else {
    rateLimited(doLoad);
  }
};

/**
 * Check if a video is the currently active one
 * @param {HTMLVideoElement} vid
 * @returns {boolean}
 */
export const isActiveVideo = (vid) => {
  if (!vid) return false;

  if (savedState.isViewerActive) {
    const activeIdx = savedState.activeIndex === -1 ? savedState.currentIndex : savedState.activeIndex;
    return savedState.cards[activeIdx]?.contains(vid) ?? false;
  }

  const activeIdx = playerState.activeIndex === -1 ? playerState.currentIndex : playerState.activeIndex;
  return getCards()[activeIdx]?.contains(vid) ?? false;
};

/**
 * Play a video
 * @param {HTMLVideoElement} vid
 */
export const playVideo = async (vid) => {
  if (!vid) return;
  const active = isActiveVideo(vid);
  if (active) ensureVideoVisible(vid);

  try {
    vid.muted =
      isVastAdVideo(vid) ||
      playerState.globalMuted ||
      !active ||
      !hasUserActivation();
    vid.volume = vid.muted ? 0 : 1;
    if (vid.paused) {
      const playPromise = vid.play();
      if (playPromise !== undefined) {
        await playPromise;
      }
    }
  } catch (err) {
    if (err.name !== "AbortError") {
      const needsGesture =
        err.name === "NotAllowedError" ||
        /user didn't interact|user gesture|not allowed/i.test(err.message || "");
      if (!needsGesture) {
        console.warn(`[playVideo] Play failed:`, err.message);
      }
      try {
        vid.muted = true;
        if (vid.paused) await vid.play();
      } catch (mutedErr) {
        if (!needsGesture && mutedErr.name !== "AbortError") {
          console.warn(`[playVideo] Muted fallback failed:`, mutedErr.message);
        }
        if (isVastAdVideo(vid) && mutedErr.name !== "AbortError") {
          failVastPlayback(vid, `Muted playback failed: ${mutedErr.message}`);
        }
      }
    }
  }
};

/**
 * Pause a video and reset to beginning
 * @param {HTMLVideoElement} vid
 */
export const pauseVideo = (vid) => {
  if (!vid) return;
  vid.pause();
  resetVideoTime(vid);
};

/**
 * Reset video playback position to start
 * @param {HTMLVideoElement} vid
 */
export const resetVideoTime = (vid) => {
  try {
    if (vid.readyState > 0) {
      vid.currentTime = 0;
    } else {
      vid.addEventListener(
        "loadedmetadata",
        () => {
          try {
            vid.currentTime = 0;
          } catch { }
        },
        { once: true },
      );
    }
  } catch { }
};

// ==========================================================================
// Mute Control
// ==========================================================================

/**
 * Update mute state for all videos
 */
export const updateAllMuteStates = () => {
  const cards = savedState.isViewerActive ? savedState.cards : getCards();
  const activeIdx = savedState.isViewerActive
    ? savedState.activeIndex
    : playerState.activeIndex;

  cards.forEach((card, idx) => {
    const vid = getVideo(card);
    if (!vid) return;
    const isActive = idx === activeIdx;
    vid.muted =
      isVastAdVideo(vid) ||
      playerState.globalMuted ||
      !isActive ||
      !hasUserActivation();
    vid.volume = vid.muted ? 0 : 1;
  });

  updateMuteIcon();
  updateMuteAvailability();
};

/**
 * Toggle global mute state
 */
export const toggleMute = () => {
  playerState.globalMuted = !playerState.globalMuted;
  localStorage.setItem("globalMuted", JSON.stringify(playerState.globalMuted));
  updateAllMuteStates();

  // Try to play unmuted
  if (!playerState.globalMuted) {
    const cards = savedState.isViewerActive ? savedState.cards : getCards();
    const activeIdx = savedState.isViewerActive
      ? savedState.activeIndex
      : playerState.activeIndex;
    const vid = getVideo(cards[activeIdx]);
    if (vid && hasUserActivation()) playVideo(vid);
  }
};

const removeFailedAdCard = (vid) => {
  const card = vid?.closest(".card");
  if (!card) return;

  const cards = getCards();
  const failedIndex = cards.indexOf(card);
  if (failedIndex === -1) return;

  const wasCurrent =
    failedIndex === playerState.currentIndex ||
    failedIndex === playerState.activeIndex;
  card.remove();

  const remainingCards = getCards();
  if (!remainingCards.length) return;

  if (failedIndex < playerState.currentIndex) {
    playerState.currentIndex--;
  }
  if (failedIndex < playerState.activeIndex) {
    playerState.activeIndex--;
  }

  if (wasCurrent) {
    const nextIndex = clamp(failedIndex, 0, remainingCards.length - 1);
    playerState.currentIndex = nextIndex;
    playerState.activeIndex = -1;
    playerState.isAnimating = false;
    playerState.animationId = (playerState.animationId || 0) + 1;
    positionCards(nextIndex, true);
    activateCard(nextIndex);
  } else {
    positionCards(playerState.currentIndex, true);
  }
};

// ==========================================================================
// Card Creation
// ==========================================================================

let adCardSerial = 0;

export const isAdCard = (card) => card?.dataset?.type === "ad";

export const createVerticalVastAdCard = () => {
  adCardSerial++;
  return createCard({
    type: "ad",
    slug: `exoclick-vast-${adCardSerial}`,
    name: "Advertisement",
    title: "Advertisement",
    author: "",
    thumbnail: "",
  });
};

/**
 * Create a video card element
 * @param {Object} videoData - Video data from feed
 * @returns {HTMLElement}
 */
export const createCard = (videoData) => {
  const card = createElement("section", "card");
  Object.assign(card.dataset, {
    type: videoData.type || "video",
    slug: videoData.slug,
    name: videoData.name || "",
    category: videoData.category || "",
    categoryLabel: videoData.categoryLabel || "",
    title: videoData.title,
    author: videoData.author,
    likes:
      videoData.type === "ad" || videoData.likes == null
        ? ""
        : String(Number(videoData.likes) || 0),
    redgifsUrl: videoData.redgifsUrl || "",
    thumbnail: videoData.thumbnail || "",
  });

  const inner = createElement("div", "inner");

  const vid = createElement("video");
  vid.playsInline = true;
  vid.muted = true;
  vid.loop = videoData.type !== "ad";
  vid.preload = "metadata";
  if (videoData.type === "ad") {
    vid.crossOrigin = "anonymous";
    vid.referrerPolicy = "strict-origin-when-cross-origin";
    vid.dataset.vastAd = "1";
  } else {
    vid.referrerPolicy = "no-referrer";
    vid.dataset.src = videoData.videoSrc || buildVideoUrl(videoData.name);
  }
  vid.dataset.errs = "0";

  const spinner = createElement("div", "spinner");
  const errEl = createElement("div", "err");

  if (videoData.type === "ad") {
    card.addEventListener("click", (event) => {
      if (event.target === vid || event.target === inner) {
        trackVastClick(vid);
      }
    });
  }

  // Event handlers
  const hideSpinner = () => {
    spinner.classList.add("hidden");
    setTimeout(() => spinner.classList.add("gone"), 150);
  };

  const clearError = () => {
    errEl.style.display = "none";
    errEl.textContent = "";
  };

  vid.addEventListener(
    "loadeddata",
    () => {
      hideSpinner();
      clearError();
      ensureVideoVisible(vid);
      if (isVastAdVideo(vid)) trackVastLoaded(vid);
      if (isActiveVideo(vid)) playVideo(vid);
    }
  );

  vid.addEventListener(
    "canplay",
    () => {
      ensureVideoVisible(vid);
      markVideoAudioChecked(vid);
    }
  );

  vid.addEventListener("playing", clearError);
  vid.addEventListener("playing", () => {
    setTimeout(() => {
      markVideoAudioChecked(vid, true);
    }, 250);
  });
  vid.addEventListener("playing", () => {
    if (isVastAdVideo(vid)) trackVastPlaying(vid);
  });
  vid.addEventListener("timeupdate", () => {
    if (isVastAdVideo(vid)) trackVastProgress(vid);
  });

  vid.addEventListener("ended", () => {
    if (isVastAdVideo(vid)) trackVastComplete(vid);
    resetVideoTime(vid);
    if (isActiveVideo(vid)) playVideo(vid);
  });

  ["waiting", "stalled", "suspend"].forEach((event) => {
    vid.addEventListener(event, () => {
      if (isActiveVideo(vid)) playVideo(vid);
    });
  });

  vid.addEventListener("error", () => {
    if (isVastAdVideo(vid)) {
      failVastPlayback(vid);
      return;
    }

    if (!isActiveVideo(vid)) {
      vid.dataset.errs = "0";
      return;
    }

    const tries = Number(vid.dataset.errs || "0");
    if (tries < 2) {
      vid.dataset.errs = String(tries + 1);
      spinner.classList.remove("gone", "hidden");
      clearError();
      vid.pause();
      resetVideoTime(vid);
      vid.load();
      playVideo(vid);

      setTimeout(() => {
        if (vid.paused || vid.readyState < 2) {
          hideSpinner();
          errEl.style.display = "grid";
          errEl.textContent = "Error. Tap to skip.";
          card.addEventListener("click", () => scrollToNext("up"), {
            once: true,
          });
        }
      }, 1500);
    } else {
      hideSpinner();
      errEl.style.display = "grid";
      errEl.textContent = "Error. Tap to skip.";
      card.addEventListener("click", () => scrollToNext("up"), { once: true });
    }
  });

  inner.append(vid, spinner, errEl);
  card.appendChild(inner);

  return card;
};

// ==========================================================================
// Card Positioning
// ==========================================================================

/**
 * Position all cards based on current index
 * @param {number} index - Current active index
 * @param {boolean} [noTransition=false] - Skip CSS transitions
 */
export const positionCards = (index, noTransition = false) => {
  const cards = getCards();
  const vh = getCardHeight();

  cards.forEach((card, i) => {
    let pos;
    if (i < index - 1) pos = -2 * vh;
    else if (i === index - 1) pos = -vh;
    else if (i === index) pos = 0;
    else if (i === index + 1) pos = vh;
    else pos = 2 * vh;

    card.style.transform = translateY(pos);
    if (noTransition) card.style.transition = "none";
    card.classList.add("positioned");
  });
};

/**
 * Snap cards back to current position (rubber band effect)
 */
export const snapBack = () => {
  const cards = getCards();
  const index = playerState.currentIndex;
  const vh = getCardHeight();
  const neighbors = getNeighborCards(index);

  neighbors.forEach((card) => {
    card.style.willChange = "transform";
    card.style.transition = "transform 260ms ease-out";
  });

  if (cards[index - 1]) {
    cards[index - 1].style.transform = translateY(-vh);
    cards[index - 1].classList.add("positioned");
  }
  if (cards[index]) {
    cards[index].style.transform = translateY(0);
    cards[index].classList.add("positioned");
  }
  if (cards[index + 1]) {
    cards[index + 1].style.transform = translateY(vh);
    cards[index + 1].classList.add("positioned");
  }

  setTimeout(() => {
    neighbors.forEach((card) => {
      card.style.transition = "none";
      card.style.willChange = "auto";
    });
  }, 300);
};

// ==========================================================================
// Card Activation
// ==========================================================================

/**
 * Activate a card (play video, update UI)
 * @param {number} index - Card index to activate
 * @param {Function} [onNeedMore] - Callback when more cards needed
 */
export const activateCard = (index, onNeedMore) => {
  const cards = getCards();
  if (!cards.length) return;

  index = clamp(index, 0, cards.length - 1);
  if (index === playerState.activeIndex) return;

  console.log(`[Player] Activating card ${index} of ${cards.length}`);

  // Pause all other videos
  cards.forEach((card, i) => {
    const vid = getVideo(card);
    if (!vid) return;
    if (i !== index) {
      vid.pause();
      resetVideoTime(vid);
      vid.muted = true;

      // Aggressively unload videos > 3 cards away to free up network
      if (Math.abs(i - index) > 3 && vid.hasAttribute("src")) {
        vid.removeAttribute("src");
        vid.load();
      }

      const err = card.querySelector(".err");
      if (err) {
        err.style.display = "none";
        err.textContent = "";
      }
    }
  });

  const card = cards[index];
  const vid = getVideo(card);

  // Update header
  updateHeader({
    slug: isAdCard(card) ? "" : card.dataset.slug || "",
    categoryLabel: card.dataset.categoryLabel || "",
    author: card.dataset.author || "",
    title: card.dataset.title || "",
    likes: card.dataset.likes,
    isLiked: isCardLiked(card),
  });

  // Preload neighbors
  getNeighborCards(index).forEach((c) => {
    const v = getVideo(c);
    if (v) loadVideo(v);
  });

  playerState.activeIndex = index;
  updateAllMuteStates();

  if (vid) {
    loadVideo(vid, true);
    vid.dataset.errs = "0";
    if (!isVastAdVideo(vid) || vid.dataset.src) {
      playVideo(vid);
      addToCache(vid);
      pruneCache();
    }
  }

  // Check if we need more cards
  const remaining = cards.length - 1 - index;
  console.log(
    `[Player] Cards remaining after current: ${remaining}, threshold: ${CONFIG.PREFETCH_THRESHOLD}`,
  );
  if (onNeedMore && remaining <= CONFIG.PREFETCH_THRESHOLD) {
    console.log(`[Player] Triggering onNeedMore - need more content!`);
    onNeedMore();
  }
};

// ==========================================================================
// Scrolling
// ==========================================================================

/**
 * Scroll to next/previous card
 * @param {"up"|"down"} direction - Scroll direction
 * @param {Function} [onNeedMore] - Callback when more cards needed
 */
export const scrollToNext = async (direction, onNeedMore) => {
  const myId = (playerState.animationId || 0) + 1;
  playerState.animationId = myId;

  const cards = getCards();
  if (!cards.length) return;

  const next =
    direction === "up"
      ? playerState.currentIndex + 1
      : playerState.currentIndex - 1;

  if (next < 0) {
    snapBack();
    return;
  }

  // Prevent scrolling past the last card
  if (next >= cards.length) {
    snapBack();
    // Trigger loading more content if at the end
    if (onNeedMore && cards.length > 0) {
      const lastIndex = cards.length - 1;
      if (lastIndex - playerState.currentIndex <= CONFIG.PREFETCH_THRESHOLD) {
        onNeedMore();
      }
    }
    return;
  }

  playerState.isAnimating = true;
  playerState.currentIndex = next;
  activateCard(next, onNeedMore);

  const vh = getCardHeight();
  const neighbors = getNeighborCards(next);

  neighbors.forEach((card) => {
    card.style.willChange = "transform";
    card.style.transition = "transform 300ms cubic-bezier(0.2,0.8,0.2,1)";
  });

  const idx = next;
  if (cards[idx - 1]) {
    cards[idx - 1].style.transform = translateY(-vh);
    cards[idx - 1].classList.add("positioned");
  }
  if (cards[idx]) {
    cards[idx].style.transform = translateY(0);
    cards[idx].classList.add("positioned");
  }
  if (cards[idx + 1]) {
    cards[idx + 1].style.transform = translateY(vh);
    cards[idx + 1].classList.add("positioned");
  }

  await sleep(300);

  if (playerState.animationId !== myId) return;

  positionCards(next, true);

  neighbors.forEach((card) => {
    card.style.transition = "none";
    card.style.willChange = "auto";
  });

  playerState.isAnimating = false;
};

// ==========================================================================
// Gesture Handling
// ==========================================================================

/**
 * Handle pointer down event
 * @param {PointerEvent} ev
 */
export const handlePointerDown = (ev) => {
  let startY = ev.clientY;

  if (playerState.isAnimating) {
    // Interrupt animation
    playerState.animationId = (playerState.animationId || 0) + 1;
    playerState.isAnimating = false;

    const cards = getCards();
    const idx = playerState.currentIndex;
    const currentCard = cards[idx];

    // Stop transitions
    getNeighborCards(idx).forEach((c) => {
      if (c) c.style.transition = "none";
    });

    // Calculate current position to resume gesture seamlessly
    const currentDy = getTranslateY(currentCard);
    startY = ev.clientY - currentDy;
  }

  ev.preventDefault();
  elements.scroller?.setPointerCapture?.(ev.pointerId);

  playerState.gesture.active = true;
  playerState.gesture.startX = ev.clientX;
  playerState.gesture.startY = startY;
  playerState.gesture.lastY = ev.clientY;
  playerState.gesture.velocity = 0;
  playerState.gesture.lastTime = performance.now();
  playerState.gesture.directionLocked = false;

  getNeighborCards(playerState.currentIndex).forEach((card) => {
    card.style.willChange = "transform";
  });
};

/**
 * Handle pointer move event
 * @param {PointerEvent} ev
 * @param {Function} [onNeedMore] - Callback when more cards needed
 */
export const handlePointerMove = (ev, onNeedMore) => {
  ev.preventDefault();

  if (
    !playerState.gesture.active ||
    playerState.isAnimating
  ) {
    return;
  }

  const dx = ev.clientX - playerState.gesture.startX;
  const dyRaw = ev.clientY - playerState.gesture.startY;

  if (!playerState.gesture.directionLocked) {
    if (Math.abs(dx) > Math.abs(dyRaw) && Math.abs(dx) > 10) {
      playerState.gesture.active = false;
      elements.scroller?.releasePointerCapture?.(ev.pointerId);
      snapBack();
      getNeighborCards(playerState.currentIndex).forEach((card) => {
        card.style.willChange = "auto";
      });
      return;
    }
    if (Math.abs(dyRaw) > 10) {
      playerState.gesture.directionLocked = true;
    }
  }

  const now = performance.now();
  let dy = dyRaw;
  const vh = getCardHeight();
  const cards = getCards();

  // Rubber band effect at edges
  if (playerState.currentIndex === 0 && dy > 0) dy *= 0.28;
  if (playerState.currentIndex === cards.length - 1 && dy < 0) dy *= 0.28;

  // Calculate velocity
  const dt = Math.max(1, now - playerState.gesture.lastTime);
  playerState.gesture.velocity =
    0.88 * playerState.gesture.velocity +
    0.12 * ((ev.clientY - playerState.gesture.lastY) / dt);

  playerState.gesture.lastY = ev.clientY;
  playerState.gesture.lastTime = now;

  // Move cards
  const idx = playerState.currentIndex;
  if (cards[idx - 1]) cards[idx - 1].style.transform = translateY(dy - vh);
  if (cards[idx]) cards[idx].style.transform = translateY(dy);
  if (cards[idx + 1]) cards[idx + 1].style.transform = translateY(dy + vh);
};

/**
 * Handle pointer up event
 * @param {PointerEvent} ev
 * @param {Function} [onNeedMore] - Callback when more cards needed
 * @param {Function} [onDoubleTap] - Callback for double tap (like)
 */
export const handlePointerUp = (ev, onNeedMore, onDoubleTap) => {
  if (!playerState.gesture.active) return;

  playerState.gesture.active = false;
  elements.scroller?.releasePointerCapture?.(ev.pointerId);

  const dy = ev.clientY - playerState.gesture.startY;
  const ady = Math.abs(dy);
  const vh = getCardHeight() * 0.33;
  const swift = Math.abs(playerState.gesture.velocity) > 0.35;
  const minDist = 30;
  const shouldScroll = swift && ady > minDist;

  // Double tap detection for liking
  const isTap = ady < 10 && !swift;
  const now = performance.now();

  if (
    isTap &&
    now - playerState.doubleTap.lastTime < 280 &&
    playerState.currentIndex === playerState.doubleTap.lastIndex
  ) {
    if (onDoubleTap) onDoubleTap(ev);
    playerState.doubleTap.lastTime = 0;
  } else if (isTap) {
    playerState.doubleTap.lastTime = now;
    playerState.doubleTap.lastIndex = playerState.currentIndex;
  }

  // Determine if we should scroll
  if (ady > vh || shouldScroll) {
    scrollToNext(dy < 0 ? "up" : "down", onNeedMore);
  } else {
    snapBack();
  }

  getNeighborCards(playerState.currentIndex).forEach((card) => {
    card.style.willChange = "auto";
  });
};

/**
 * Handle wheel event
 * @param {WheelEvent} ev
 * @param {Function} [onNeedMore] - Callback when more cards needed
 */
export const handleWheel = (ev, onNeedMore) => {
  if (isGateVisible() || playerState.isAnimating) {
    return;
  }

  const dy = ev.deltaY || 0;
  if (Math.abs(dy) < 25) return;

  ev.preventDefault();

  if (playerState.wheelBusy) return;
  playerState.wheelBusy = true;

  scrollToNext(dy > 0 ? "up" : "down", onNeedMore);

  setTimeout(() => {
    playerState.wheelBusy = false;
  }, 360);
};

// ==========================================================================
// Keyboard Handling
// ==========================================================================

/**
 * Handle keyboard navigation
 * @param {KeyboardEvent} ev
 * @param {Object} callbacks
 */
export const handleKeyDown = (ev, callbacks = {}) => {
  if (isGateVisible()) return;

  if (["ArrowDown", "PageDown", " "].includes(ev.key)) {
    ev.preventDefault();
    scrollToNext("up", callbacks.onNeedMore);
  } else if (["ArrowUp", "PageUp"].includes(ev.key)) {
    ev.preventDefault();
    scrollToNext("down", callbacks.onNeedMore);
  }
};

// ==========================================================================
// Visibility Handling
// ==========================================================================

/**
 * Handle visibility change (pause when hidden, play when visible)
 */
export const handleVisibilityChange = () => {
  const cards = savedState.isViewerActive ? savedState.cards : getCards();
  const activeIdx = savedState.isViewerActive
    ? savedState.activeIndex
    : playerState.activeIndex;
  const vid = getVideo(cards[activeIdx]);

  if (!vid) return;

  if (document.hidden) {
    vid.pause();
  } else {
    playVideo(vid);
  }
};

// ==========================================================================
// Resize Handling
// ==========================================================================

/**
 * Handle window resize
 */
export const handleResize = () => {
  positionCards(playerState.currentIndex, true);
};
